import {
    addDays,
    endOfDay,
    fromUnixTime,
    getDate,
    getDay,
    isAfter,
    isBefore,
    startOfDay,
} from 'date-fns';
import type {
    DatePeriodData,
    DayOfWeek,
    MonthlyRecurrenceData,
    WeeklyRecurrenceData,
    YearlyRecurrenceData,
    GetProductsResItem0ResolvedSchedulesItemDto,
} from '@travelity/api/generated';
import { ScheduleType } from '@travelity/api/generated';
import type { ProductMinimal } from '@travelity/api/services';

const dayMap: Record<DayOfWeek, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

const toDayKey = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const yearlyDaySetCache = new Map<string, Set<string>>();

const getYearlyAllowedDaySet = (
    recurrence: YearlyRecurrenceData
): Set<string> => {
    const raw = recurrence.dates || [];
    const cacheKey = raw.join(',');
    const cached = yearlyDaySetCache.get(cacheKey);
    if (cached) return cached;

    const allowed = new Set(
        raw.map(ts => toDayKey(startOfDay(fromUnixTime(ts))))
    );
    yearlyDaySetCache.set(cacheKey, allowed);
    return allowed;
};

const isDayWithinPeriod = (day: Date, period: DatePeriodData): boolean => {
    const start = startOfDay(fromUnixTime(period.start));
    const end = period.end ? endOfDay(fromUnixTime(period.end)) : null;

    if (isBefore(day, start)) return false;
    if (end && isAfter(day, end)) return false;
    return true;
};

const matchesRecurrence = (
    day: Date,
    recurrence?:
        | MonthlyRecurrenceData
        | WeeklyRecurrenceData
        | YearlyRecurrenceData
): boolean => {
    if (!recurrence) return true;

    if (recurrence.type === 'weekly') {
        const allowed = recurrence.days_of_week || [];
        return allowed.map(d => dayMap[d]).includes(getDay(day));
    }

    if (recurrence.type === 'monthly') {
        const allowed = recurrence.days_of_month || [];
        return allowed.includes(getDate(day));
    }

    const allowedDays = getYearlyAllowedDaySet(recurrence);
    return allowedDays.has(toDayKey(startOfDay(day)));
};

const isScheduleAvailableOnDay = (
    schedule: GetProductsResItem0ResolvedSchedulesItemDto,
    day: Date
): boolean => {
    const inAnyWindow = (schedule.dates || []).some(period =>
        isDayWithinPeriod(day, period)
    );
    if (!inAnyWindow) return false;

    if (schedule.type === ScheduleType.RECURRING) {
        return matchesRecurrence(day, schedule.recurrence);
    }

    return matchesRecurrence(day, schedule.recurrence);
};

const isProductAvailableOnDay = (
    product: ProductMinimal,
    day: Date
): boolean => {
    const schedules = product.resolvedSchedules || [];
    if (schedules.length === 0) return false;
    return schedules.some(schedule => isScheduleAvailableOnDay(schedule, day));
};

const getProductWindow = (
    product: ProductMinimal,
    horizonEnd: Date
): { start: Date; end: Date } | null => {
    const schedules = product.resolvedSchedules || [];
    const periods = schedules.flatMap(schedule => schedule.dates || []);
    if (periods.length === 0) return null;

    let minStart: Date | null = null;
    let maxEnd: Date | null = null;

    periods.forEach(period => {
        const start = startOfDay(fromUnixTime(period.start));
        const end = period.end ? endOfDay(fromUnixTime(period.end)) : null;

        if (!minStart || start < minStart) minStart = start;

        if (end) {
            if (!maxEnd || end > maxEnd) maxEnd = end;
        } else {
            maxEnd = horizonEnd;
        }
    });

    if (!minStart) return null;
    const safeEnd = maxEnd ?? horizonEnd;
    return { start: minStart, end: safeEnd };
};

export const hasCommonAvailableDay = (
    products: ProductMinimal[],
    horizonDays = 365
): boolean => {
    if (products.length <= 1) return true;

    const today = startOfDay(new Date());
    const horizonEnd = endOfDay(addDays(today, horizonDays));

    const windows = products
        .map(product => getProductWindow(product, horizonEnd))
        .filter((window): window is { start: Date; end: Date } =>
            Boolean(window)
        );

    if (windows.length !== products.length) return false;

    const windowStart = windows.reduce(
        (max, current) => (current.start > max ? current.start : max),
        windows[0].start
    );
    const windowEnd = windows.reduce(
        (min, current) => (current.end < min ? current.end : min),
        windows[0].end
    );

    const effectiveStart = windowStart > today ? windowStart : today;
    const effectiveEnd = windowEnd < horizonEnd ? windowEnd : horizonEnd;

    if (effectiveStart > effectiveEnd) return false;

    const availabilityCache = new Map<string, Set<string>>();

    for (
        let cursor = effectiveStart;
        !isAfter(cursor, effectiveEnd);
        cursor = addDays(cursor, 1)
    ) {
        const dayKey = toDayKey(cursor);
        const allAvailable = products.every(product => {
            const cacheKey = product.id;
            const cached = availabilityCache.get(cacheKey);
            if (cached?.has(dayKey)) return true;
            const available = isProductAvailableOnDay(product, cursor);
            if (available) {
                if (cached) {
                    cached.add(dayKey);
                } else {
                    availabilityCache.set(cacheKey, new Set([dayKey]));
                }
            }
            return available;
        });

        if (allAvailable) return true;
    }

    return false;
};
